import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Button, Text } from '@calm-web/design-system';

import messages from '@/components/emotion-detector/messages';
import { BaseCard } from '@/components/emotion-detector/shared/BaseCard';
import { useEmotionChat } from '@/hooks/emotion-detector';

import ChatMessage from './components/ChatMessage';
import { TEXTAREA_ROWS } from './constants';
import { ChatContainer, ChatHeader, ChatTextarea, InputContainer, MessagesContainer } from './styles';

interface EmotionChatProps {
	emotionChat: ReturnType<typeof useEmotionChat>;
	isVisible: boolean;
}

export default function EmotionChat({ emotionChat, isVisible }: EmotionChatProps) {
	const { formatMessage } = useIntl();
	const { chatMessages, input, sending, setInput, handleSend } = emotionChat;

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setInput(e.target.value);
		},
		[setInput],
	);

	const handleTextareaClick = useCallback((e: React.MouseEvent<HTMLTextAreaElement>) => {
		e.currentTarget.focus();
	}, []);

	if (!isVisible) return null;

	return (
		<BaseCard data-card="chat">
			<ChatContainer>
				<ChatHeader>
					<Text el="h2">{formatMessage(messages.cameraChatTitle)}</Text>
				</ChatHeader>

				<MessagesContainer>
					{chatMessages.map((message, idx) => (
						<ChatMessage
							key={`${message.role}-${idx}`}
							message={message}
							messageId={`${message.role}-${idx}`}
						/>
					))}
				</MessagesContainer>

				<InputContainer>
					<ChatTextarea
						value={input}
						onChange={handleInputChange}
						placeholder={formatMessage(messages.cameraChatPlaceholder)}
						disabled={sending}
						rows={TEXTAREA_ROWS}
						onClick={handleTextareaClick}
					/>
					<Button
						backgroundColor="accessibleBrandGradient"
						onPress={handleSend}
						isDisabled={sending || !input.trim()}
					>
						{sending ? formatMessage(messages.cameraChatSending) : formatMessage(messages.cameraChatSend)}
					</Button>
				</InputContainer>
			</ChatContainer>
		</BaseCard>
	);
}
